import type { InvitationAcceptanceFailedAction } from '@Application/Security/Invitations/Actions/invitationAcceptanceFailed';
import type { InvitationFetchFailedAction } from '@Application/Security/Invitations/Actions/invitationFetchFailed';
import type { InvitationPayload } from '@Application/Security/Invitations/Actions/invitationFetched';
import acceptInvitation from '@Application/Security/Invitations/acceptInvitation';
import AcceptInvitation from '@Application/Security/Invitations/Commands/AcceptInvitation';
import fetchInvitation from '@Application/Security/Invitations/fetchInvitation';
import FetchInvitation from '@Application/Security/Invitations/Queries/FetchInvitation';
import AuthenticationNotifications from '@UI/Pages/Authentication/Shared/AuthenticationNotifications';
import InvitationBySso from '@UI/Pages/Authentication/Invitation/InvitationBySso';
import InvitationDetails from '@UI/Pages/Authentication/Invitation/InvitationDetails';
import InvitationGone from '@UI/Pages/Authentication/Invitation/InvitationGone';
import InvitationUnavailable from '@UI/Pages/Authentication/Invitation/InvitationUnavailable';
import NewPasswordFormData from '@UI/Pages/Authentication/Shared/FormData/NewPasswordFormData';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch } from '@verclam/synapse';
import { Card, Message, Spinner } from '@verclam/ui-kit';

type InvitationState =
	| { readonly type: 'loading' }
	| { readonly type: 'ready'; readonly invitation: InvitationPayload }
	| { readonly type: 'expired'; readonly message: string }
	| { readonly type: 'invalid' };

/** Sole entry point creating an account: no self-registration screen exists anywhere else. */
const InvitationPage: React.FC = () => {
	const { token } = useParams<{ token: string }>();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [state, setState] = React.useState<InvitationState>({ type: 'loading' });
	const [formData, setFormData] = React.useState(() => NewPasswordFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (token === undefined) {
			setState({ type: 'invalid' });
			return undefined;
		}

		let isMounted = true;

		dispatch(fetchInvitation(new FetchInvitation(token)))
			.then((action) => {
				if (!isMounted) {
					return;
				}

				// A response that isn't `INVITATION_FETCHED` never reaches this line in practice, but the
				// dispatch return type still allows it: treat it as an invalid link rather than leaving the
				// spinner running forever with nothing to show for it.
				if (action.type !== 'INVITATION_FETCHED') {
					setState({ type: 'invalid' });
					return;
				}

				setState({ type: 'ready', invitation: action.payload });
			})
			.catch((action: InvitationFetchFailedAction) => {
				if (!isMounted) {
					return;
				}

				if (action.reason === 'gone') {
					setState({ type: 'expired', message: action.message });
					return;
				}

				setState({ type: 'invalid' });
			});

		return () => {
			isMounted = false;
		};
	}, [dispatch, token]);

	const handlePasswordChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPassword(value));
	}, []);

	const handlePasswordConfirmationChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPasswordConfirmation(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (token === undefined) {
			return;
		}

		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		setIsSubmitting(true);

		dispatch(acceptInvitation(new AcceptInvitation(token, formData.toCommandData().password)))
			.then((action) => {
				if (action.type === 'SECOND_FACTOR_ENROLLMENT_REQUIRED') {
					// The challenge cookie is already set by the server: `RequireChallenge purpose="enrollment"`
					// lets this navigation straight through.
					navigate('/login/enrollment', { replace: true });
					return;
				}

				if (action.type === 'USER_AUTHENTICATED') {
					// The session cookies are already set by the server: `RequireAnonymous` would redirect here
					// on its own, but navigating explicitly states the intent instead of leaving it to a guard.
					navigate('/', { replace: true });
					return;
				}

				// `INVITATION_ACCEPTED` (the `mfa_required` branch of `LoginResponse`) has no path to this
				// screen — an invited user never carries an activated credential — but the type stays part
				// of the contract: `/login` is the safe fallback if the server ever takes it.
				navigate('/login', { replace: true });
			})
			.catch((action: InvitationAcceptanceFailedAction) => {
				if (action.reason === 'invalid') {
					updateValidationFromExternalSource({
						action: {
							type: action.type,
							payload: { fieldErrors: action.payload?.fieldErrors ?? [] },
						},
						eventNames: 'INVITATION_ACCEPTANCE_FAILED',
						formDataUpdater: setFormData,
					});
					return;
				}

				if (action.reason === 'unknown') {
					// A transient failure (server error, network cut) is no proof the invitation is dead:
					// leave the form and the password the visitor typed in place rather than discard both.
					return;
				}

				// The invitation died between the `GET` and this `POST` (revoked, expired, or consumed by a
				// concurrent submission), or the token no longer resolves at all: drop the form either way.
				if (action.reason === 'gone') {
					setState({ type: 'expired', message: action.message });
					return;
				}

				setState({ type: 'invalid' });
			})
			.finally(() => setIsSubmitting(false));
	}, [dispatch, formData, navigate, token]);

	// `signInMethod` is a hand-assembled discriminant (`Application/Invitations/Actions/invitationFetched`),
	// not a registered enum: any value this front does not recognize renders as `password` rather than
	// crashing (§8.5).
	const renderInvitationBody = (invitationToRender: InvitationPayload): React.ReactElement => {
		switch (invitationToRender.signInMethod) {
			case 'identity_provider': {
				return <InvitationBySso invitation={invitationToRender} />;
			}
			case 'unavailable': {
				return <InvitationUnavailable invitation={invitationToRender} />;
			}
			default: {
				return (
					<InvitationDetails
						invitation={invitationToRender}
						formData={formData}
						onPasswordChange={handlePasswordChange}
						onPasswordConfirmationChange={handlePasswordConfirmationChange}
						onSubmit={handleSubmit}
						isSubmitting={isSubmitting}
					/>
				);
			}
		}
	};

	const renderContent = (): React.ReactElement => {
		switch (state.type) {
			case 'loading': {
				return <Spinner aria-label="Chargement de l’invitation" />;
			}
			case 'expired': {
				return <InvitationGone message={state.message} />;
			}
			case 'invalid': {
				return <Message severity="error" text="Ce lien d'invitation est invalide." />;
			}
			case 'ready': {
				return renderInvitationBody(state.invitation);
			}
		}
	};

	return (
		<React.Fragment>
			<Card title="Invitation" width="100%" maxWidth="28rem">
				{renderContent()}
			</Card>
			<AuthenticationNotifications />
		</React.Fragment>
	);
};

export default InvitationPage;
